import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import ConfigPanel from "./AdminConfig";

const API = process.env.REACT_APP_API_URL;

export default function AdminDashboard() {
  const { token, user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("mfds");
  const [mfds, setMfds] = useState([]);
  const [msg, setMsg] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [editMfd, setEditMfd] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showEditPw, setShowEditPw] = useState(false);
  const [newMfd, setNewMfd] = useState({ name:"", email:"", phone:"", password:"", arn:"", firm_name:"", access_start:"", access_end:"", branding:"no" });
  const headers = { Authorization: "Bearer " + token };
  const [diagResults, setDiagResults] = useState({});
  const [diagLoading, setDiagLoading] = useState({});
  const [actionMsg, setActionMsg] = useState({});
  const [logData, setLogData] = useState({});

  const fetchMfds = async () => {
    try {
      const res = await axios.get(API + "/api/auth/admin/mfds", { headers });
      setMfds(res.data.mfds || []);
    } catch { setMsg("Failed to load MFDs"); }
  };

  useEffect(() => { fetchMfds(); }, []);
  const clearMsg = () => setTimeout(() => setMsg(""), 4000);

  const createMfd = async () => {
    if (!newMfd.name || !newMfd.email || !newMfd.phone || !newMfd.password) {
      setMsg("✖ Name, Email, Phone and Password are required"); return;
    }
    try {
      const res = await axios.post(API + "/api/auth/admin/create-mfd", newMfd, { headers });
      setMsg("✔ MFD created! Code: " + res.data.mfd_code);
      setNewMfd({ name:"", email:"", phone:"", password:"", arn:"", firm_name:"", access_start:"", access_end:"", branding:"no" });
      setShowPw(false); fetchMfds(); setTab("mfds");
    } catch (e) { setMsg("✖ " + (e.response?.data?.error || "Creation failed")); }
    clearMsg();
  };

  const viewAsMfd = async (mfd_code) => {
    try {
      const res = await axios.post(API + "/api/auth/admin/view-as-mfd", { mfd_code }, { headers });
      const payload = {
        access_token: res.data.access_token,
        user: { ...res.data.user, must_change_password: false },
      };
      const encoded = encodeURIComponent(btoa(unescape(encodeURIComponent(JSON.stringify(payload)))));
      window.open(window.location.origin + "/mfd#impersonate=" + encoded, "_blank", "noopener");
    } catch (e) { setMsg("✖ " + (e.response?.data?.error || "Failed to open MFD view")); }
    clearMsg();
  };

  const openEdit = (mfd) => {
    setEditMfd(mfd);
    setEditForm({ name:mfd.name, email:mfd.email, phone:mfd.phone||"", firm_name:mfd.firm_name||"", arn:mfd.arn||"",
      access_start:mfd.access_start||"", access_end:mfd.access_end||"", branding: mfd.branding ?? "no", password:"" });
    setShowEditPw(false); setTab("edit");
  };

  const saveEdit = async () => {
    try {
      const payload = { ...editForm };
      if (!payload.password) delete payload.password;
      await axios.patch(API + "/api/auth/admin/mfd/" + editMfd.id, payload, { headers });
      setMsg("✔ MFD updated successfully");
      fetchMfds(); setTab("mfds"); setEditMfd(null);
    } catch (e) { setMsg("✖ " + (e.response?.data?.error || "Update failed")); }
    clearMsg();
  };

  const toggleActive = async (mfd) => {
    try {
      await axios.patch(API + "/api/auth/admin/toggle-user", { user_id: mfd.id }, { headers });
      setMsg("✔ " + mfd.name + " " + (mfd.is_active ? "deactivated" : "activated"));
      fetchMfds();
    } catch { setMsg("✖ Toggle failed"); }
    clearMsg();
  };

  const deleteMfd = async (mfd) => {
    if (!window.confirm("Delete MFD " + mfd.name + " (" + mfd.mfd_code + ")? This cannot be undone.")) return;
    try {
      await axios.delete(API + "/api/auth/admin/delete-user", { headers, data: { user_id: mfd.id } });
      setMsg("✔ " + mfd.name + " deleted"); fetchMfds();
    } catch { setMsg("✖ Delete failed"); }
    clearMsg();
  };

  const handleLogout = () => { logout(); navigate("/login"); };
  const filtered = mfds.filter(m =>
    (m.name||"").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (m.mfd_code||"").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (m.email||"").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (m.firm_name||"").toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div style={s.page}>
      <div style={s.navbar}>
        <span style={s.brand}>🏛 Fund Intelligence Platform</span>
        <div style={s.navRight}>
          <span style={s.navUser}>👤 {user?.name || user?.email} (Admin)</span>
          <button onClick={handleLogout} style={s.logoutBtn}>Logout</button>
        </div>
      </div>
      <div style={s.container}>
        <h2 style={s.heading}>Admin Dashboard</h2>
        {msg && <div style={msg.startsWith("✔") ? s.msgOk : s.msgErr}>{msg}</div>}
        <div style={s.tabs}>
          {[["mfds","👥 Registered MFDs"],["register","➕ Register MFD"],["config","⚙️ Platform Config"],["diagnostics","🔬 Diagnostics"]].map(([k,label]) => (
            <button key={k} onClick={() => { setTab(k); setEditMfd(null); }}
              style={tab === k ? {...s.tab, ...s.tabActive} : s.tab}>{label}</button>
          ))}
          {editMfd && <button style={{...s.tab, ...s.tabEdit}}>✏ Editing: {editMfd.mfd_code}</button>}
        </div>
        {tab === "mfds" && (
          <div style={s.card}>
            <div style={s.cardHeader}>
              <h3 style={s.cardTitle}>Registered MFDs ({filtered.length})</h3>
              <input style={s.search} placeholder="Search by name, code, email, firm..."
                value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            </div>
            {filtered.length === 0 ? <p style={s.empty}>No MFDs found.</p> : (
              <div style={s.tableWrap}>
                <table style={s.table}>
                  <thead><tr style={s.thead}>
                    <th style={s.th}>Code</th><th style={s.th}>Name</th><th style={s.th}>Firm</th>
                    <th style={s.th}>ARN</th><th style={s.th}>Email</th><th style={s.th}>Phone</th>
                    <th style={s.th}>Created</th><th style={s.th}>Access Window</th><th style={s.th}>Branding</th>
                    <th style={s.th}>Status</th><th style={s.th}>Actions</th>
                  </tr></thead>
                  <tbody>
                    {filtered.map(m => (
                      <tr key={m.id} style={s.tr}>
                        <td style={{...s.td, fontWeight:"700", color:"#1565C0"}}>{m.mfd_code||"—"}</td>
                        <td style={s.td}>{m.name}</td>
                        <td style={s.td}>{m.firm_name||"—"}</td>
                        <td style={s.td}>{m.arn||"—"}</td>
                        <td style={s.td}>{m.email}</td>
                        <td style={s.td}>{m.phone||"—"}</td>
                        <td style={s.td}>{m.created_at ? m.created_at.slice(0,10) : "—"}</td>
                        <td style={s.td}>{(m.access_start || m.access_end) ? `${m.access_start||"…"} → ${m.access_end||"…"}` : "—"}</td>
                        <td style={s.td}>{m.branding === "yes" ? "Yes" : m.branding === "no" || !m.branding ? "No" : "None"}</td>
                        <td style={s.td}><span style={m.is_active ? s.active : s.inactive}>{m.is_active ? "Active" : "Inactive"}</span></td>
                        <td style={s.td}>
                          <button onClick={() => openEdit(m)} style={s.btnEdit}>Edit</button>
                          <button onClick={() => toggleActive(m)} style={m.is_active ? s.btnDeact : s.btnAct}>{m.is_active ? "Deactivate" : "Activate"}</button>
                          <button onClick={() => deleteMfd(m)} style={s.btnDel}>Delete</button>
                          <button onClick={() => viewAsMfd(m.mfd_code)} style={s.btnFip}>FIP</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        {tab === "register" && (
          <div style={s.card}>
            <h3 style={s.cardTitle}>Register New MFD</h3>
            <div style={s.formGrid}>
              <div style={s.formGroup}><label style={s.label}>Full Name *</label>
                <input style={s.input} placeholder="e.g. Rahul Sharma" value={newMfd.name} onChange={e => setNewMfd({...newMfd, name:e.target.value})} /></div>
              <div style={s.formGroup}><label style={s.label}>Email *</label>
                <input style={s.input} type="email" placeholder="e.g. rahul@example.com" value={newMfd.email} onChange={e => setNewMfd({...newMfd, email:e.target.value})} /></div>
              <div style={s.formGroup}><label style={s.label}>Phone *</label>
                <input style={s.input} placeholder="e.g. 9876543210" value={newMfd.phone} onChange={e => setNewMfd({...newMfd, phone:e.target.value})} /></div>
              <div style={s.formGroup}><label style={s.label}>Password *</label>
                <div style={s.pwWrap}>
                  <input style={{...s.input, flex:1}} type={showPw ? "text" : "password"} autoComplete="new-password" placeholder="Min 6 characters" value={newMfd.password} onChange={e => setNewMfd({...newMfd, password:e.target.value})} />
                  <button style={s.pwToggle} onClick={() => setShowPw(!showPw)}>{showPw ? "🙈" : "👁"}</button>
                </div></div>
              <div style={s.formGroup}><label style={s.label}>ARN Number</label>
                <input style={s.input} placeholder="e.g. ARN-12345" value={newMfd.arn} onChange={e => setNewMfd({...newMfd, arn:e.target.value})} /></div>
              <div style={s.formGroup}><label style={s.label}>Firm Name</label>
                <input style={s.input} placeholder="e.g. Sharma Investments" value={newMfd.firm_name} onChange={e => setNewMfd({...newMfd, firm_name:e.target.value})} /></div>
              <div style={s.formGroup}><label style={s.label}>Access Start Date <span style={s.optional}>(optional)</span></label>
                <input style={s.input} type="date" value={newMfd.access_start||""} onChange={e => setNewMfd({...newMfd, access_start:e.target.value})} /></div>
              <div style={s.formGroup}><label style={s.label}>Access End Date <span style={s.optional}>(optional)</span></label>
                <input style={s.input} type="date" value={newMfd.access_end||""} onChange={e => setNewMfd({...newMfd, access_end:e.target.value})} /></div>
              <div style={s.formGroup}><label style={s.label}>Report Branding</label>
                <select style={s.input} value={newMfd.branding ?? "no"} onChange={e => setNewMfd({...newMfd, branding:e.target.value})}>
                  <option value="no">No (show Rumtek details)</option>
                  <option value="yes">Yes (show my firm details)</option>
                  <option value="">None (show nothing)</option>
                </select></div>
            </div>
            <div style={s.formNote}>MFD code will be auto-generated (RMF001, RMF002...). A welcome email with login credentials will be sent to the MFD.</div>
            <button onClick={createMfd} style={s.createBtn}>➕ Create MFD &amp; Send Welcome Email</button>
          </div>
        )}
        {tab === "config" && (
          <ConfigPanel token={token} API={API} />
        )}
        {tab === "diagnostics" && (
          <DiagnosticsPanel token={token} API={API} />
        )}
        {tab === "edit" && editMfd && (
          <div style={s.card}>
            <div style={s.cardHeader}>
              <h3 style={s.cardTitle}>Edit MFD — {editMfd.mfd_code}</h3>
              <button style={s.btnBack} onClick={() => { setTab("mfds"); setEditMfd(null); }}>← Back to List</button>
            </div>
            <div style={s.formGrid}>
              <div style={s.formGroup}><label style={s.label}>Full Name</label>
                <input style={s.input} value={editForm.name||""} onChange={e => setEditForm({...editForm, name:e.target.value})} /></div>
              <div style={s.formGroup}><label style={s.label}>Email</label>
                <input style={s.input} type="email" value={editForm.email||""} onChange={e => setEditForm({...editForm, email:e.target.value})} /></div>
              <div style={s.formGroup}><label style={s.label}>Phone</label>
                <input style={s.input} value={editForm.phone||""} onChange={e => setEditForm({...editForm, phone:e.target.value})} /></div>
              <div style={s.formGroup}><label style={s.label}>New Password <span style={s.optional}>(leave blank to keep current)</span></label>
                <div style={s.pwWrap}>
                  <input style={{...s.input, flex:1}} type={showEditPw ? "text" : "password"} autoComplete="new-password" placeholder="Enter new password or leave blank" value={editForm.password||""} onChange={e => setEditForm({...editForm, password:e.target.value})} />
                  <button style={s.pwToggle} onClick={() => setShowEditPw(!showEditPw)}>{showEditPw ? "🙈" : "👁"}</button>
                </div></div>
              <div style={s.formGroup}><label style={s.label}>ARN Number</label>
                <input style={s.input} value={editForm.arn||""} onChange={e => setEditForm({...editForm, arn:e.target.value})} /></div>
              <div style={s.formGroup}><label style={s.label}>Firm Name</label>
                <input style={s.input} value={editForm.firm_name||""} onChange={e => setEditForm({...editForm, firm_name:e.target.value})} /></div>
              <div style={s.formGroup}><label style={s.label}>Access Start Date <span style={s.optional}>(optional)</span></label>
                <input style={s.input} type="date" value={editForm.access_start||""} onChange={e => setEditForm({...editForm, access_start:e.target.value})} /></div>
              <div style={s.formGroup}><label style={s.label}>Access End Date <span style={s.optional}>(optional)</span></label>
                <input style={s.input} type="date" value={editForm.access_end||""} onChange={e => setEditForm({...editForm, access_end:e.target.value})} /></div>
              <div style={s.formGroup}><label style={s.label}>Report Branding</label>
                <select style={s.input} value={editForm.branding ?? "no"} onChange={e => setEditForm({...editForm, branding:e.target.value})}>
                  <option value="no">No (show Rumtek details)</option>
                  <option value="yes">Yes (show my firm details)</option>
                  <option value="">None (show nothing)</option>
                </select></div>
            </div>
            <div style={s.editActions}>
              <button onClick={saveEdit} style={s.createBtn}>💾 Save Changes</button>
              <button onClick={() => { setTab("mfds"); setEditMfd(null); }} style={s.btnCancel}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  page:       { minHeight:"100vh", background:"#f5f7fa", fontFamily:"Segoe UI, sans-serif" },
  navbar:     { background:"#1565C0", color:"#fff", padding:"14px 24px", display:"flex", justifyContent:"space-between", alignItems:"center" },
  brand:      { fontWeight:"700", fontSize:"18px" },
  navRight:   { display:"flex", alignItems:"center", gap:"16px" },
  navUser:    { fontSize:"14px" },
  logoutBtn:  { background:"#CC0000", color:"#fff", border:"none", borderRadius:"6px", padding:"8px 16px", cursor:"pointer", fontWeight:"600" },
  container:  { maxWidth:"1100px", margin:"0 auto", padding:"32px 24px" },
  heading:    { color:"#1565C0", marginBottom:"8px" },
  msgOk:      { background:"#e8f5e9", border:"1px solid #2e7d32", borderRadius:"8px", padding:"12px 16px", marginBottom:"16px", color:"#2e7d32" },
  msgErr:     { background:"#ffebee", border:"1px solid #CC0000", borderRadius:"8px", padding:"12px 16px", marginBottom:"16px", color:"#CC0000" },
  tabs:       { display:"flex", gap:"8px", marginBottom:"24px", flexWrap:"wrap" },
  tab:        { padding:"10px 20px", border:"2px solid #1565C0", borderRadius:"8px", background:"#fff", color:"#1565C0", cursor:"pointer", fontWeight:"600", fontSize:"14px" },
  tabActive:  { background:"#1565C0", color:"#fff" },
  tabEdit:    { background:"#E65100", color:"#fff", borderColor:"#E65100" },
  card:       { background:"#fff", borderRadius:"12px", padding:"24px", boxShadow:"0 2px 12px rgba(0,0,0,0.08)" },
  cardHeader: { display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"16px", flexWrap:"wrap", gap:"12px" },
  cardTitle:  { color:"#1565C0", margin:"0" },
  search:     { padding:"8px 14px", border:"1.5px solid #ddd", borderRadius:"8px", fontSize:"14px", width:"280px" },
  empty:      { color:"#999", textAlign:"center", padding:"32px" },
  tableWrap:  { overflowX:"auto" },
  table:      { width:"100%", borderCollapse:"collapse", fontSize:"13px" },
  thead:      { background:"#1565C0" },
  th:         { color:"#fff", padding:"10px 12px", textAlign:"left", whiteSpace:"nowrap" },
  tr:         { borderBottom:"1px solid #f0f0f0" },
  td:         { padding:"10px 12px", verticalAlign:"middle" },
  active:     { background:"#e8f5e9", color:"#2e7d32", padding:"3px 10px", borderRadius:"12px", fontSize:"12px" },
  inactive:   { background:"#ffebee", color:"#CC0000", padding:"3px 10px", borderRadius:"12px", fontSize:"12px" },
  btnEdit:    { background:"#1565C0", color:"#fff", border:"none", borderRadius:"6px", padding:"5px 10px", cursor:"pointer", fontSize:"12px", marginRight:"4px" },
  btnDeact:   { background:"#E65100", color:"#fff", border:"none", borderRadius:"6px", padding:"5px 10px", cursor:"pointer", fontSize:"12px", marginRight:"4px" },
  btnAct:     { background:"#2e7d32", color:"#fff", border:"none", borderRadius:"6px", padding:"5px 10px", cursor:"pointer", fontSize:"12px", marginRight:"4px" },
  btnDel:     { background:"#CC0000", color:"#fff", border:"none", borderRadius:"6px", padding:"5px 10px", cursor:"pointer", fontSize:"12px", marginRight:"4px" },
  btnFip:     { background:"#6A1B9A", color:"#fff", border:"none", borderRadius:"6px", padding:"5px 10px", cursor:"pointer", fontSize:"12px" },
  btnBack:    { background:"#546E7A", color:"#fff", border:"none", borderRadius:"6px", padding:"8px 16px", cursor:"pointer", fontSize:"13px" },
  btnCancel:  { background:"#546E7A", color:"#fff", border:"none", borderRadius:"8px", padding:"12px 24px", cursor:"pointer", fontWeight:"600", fontSize:"14px", marginLeft:"12px" },
  formGrid:   { display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(280px,1fr))", gap:"20px", marginBottom:"20px" },
  formGroup:  { display:"flex", flexDirection:"column", gap:"6px" },
  label:      { fontWeight:"600", fontSize:"13px", color:"#444" },
  optional:   { fontWeight:"400", color:"#999", fontSize:"12px" },
  input:      { padding:"10px 12px", border:"1.5px solid #ddd", borderRadius:"8px", fontSize:"14px", outline:"none" },
  pwWrap:     { display:"flex", alignItems:"center", gap:"8px" },
  pwToggle:   { background:"none", border:"1.5px solid #ddd", borderRadius:"8px", padding:"8px 10px", cursor:"pointer", fontSize:"16px" },
  formNote:   { background:"#E3F0FF", border:"1px solid #1565C0", borderRadius:"8px", padding:"10px 14px", fontSize:"13px", color:"#1565C0", marginBottom:"20px" },
  createBtn:  { background:"#1565C0", color:"#fff", border:"none", borderRadius:"8px", padding:"12px 28px", fontWeight:"600", fontSize:"14px", cursor:"pointer" },
  editActions:{ display:"flex", alignItems:"center", marginTop:"8px" },
};

function DiagnosticsPanel({ token, API }) {
  const headers = { Authorization: "Bearer " + token };
  const [results, setResults] = React.useState({});
  const [loading, setLoading] = React.useState({});
  const [actionMsg, setActionMsg] = React.useState({});
  const [logData, setLogData] = React.useState(null);
  const [logName, setLogName] = React.useState("");

  const GROUPS = [
    { key: "connectivity",   label: "🌐 Group 1 — Connectivity",       endpoint: "/api/admin/diag/connectivity" },
    { key: "pipeline",       label: "⚙️ Group 2 — Pipeline Status",     endpoint: "/api/admin/diag/pipeline" },
    { key: "data-quality",   label: "📊 Group 3 — Data Quality",        endpoint: "/api/admin/diag/data-quality" },
    { key: "scoring-matrix", label: "🎯 Group 4 — Scoring Matrix",      endpoint: "/api/admin/diag/scoring-matrix" },
    { key: "reports-health", label: "📋 Group 5 — Reports Health",      endpoint: "/api/admin/diag/reports-health" },
    { key: "mfd-activity",   label: "👥 Group 6 — MFD Activity",        endpoint: "/api/admin/diag/mfd-activity" },
    { key: "security",       label: "🔒 Group 7 — Server Security",     endpoint: "/api/admin/diag/security" },
    { key: "last-audit",     label: "🗓️ Group 8 — Last Monthly Audit",  endpoint: "/api/admin/diag/last-audit" },
  ];

  const ACTIONS = [
    { key: "phase1", label: "▶ Run Phase 1",        endpoint: "/api/admin/action/run-phase1", color: "#1565C0" },
    { key: "phase2", label: "▶ Run Phase 2",        endpoint: "/api/admin/action/run-phase2", color: "#1565C0" },
    { key: "phase3", label: "▶ Run Phase 3",        endpoint: "/api/admin/action/run-phase3", color: "#1565C0" },
    { key: "aum",    label: "💰 Fetch AUM",          endpoint: "/api/admin/action/run-aum",    color: "#2E7D32" },
    { key: "ter",    label: "📉 Fetch TER",          endpoint: "/api/admin/action/run-ter",    color: "#2E7D32" },
    { key: "sheets", label: "📤 Push to Sheets",    endpoint: "/api/admin/action/run-sheets", color: "#6A1B9A" },
    { key: "full",   label: "🚀 Full Monthly Run",  endpoint: "/api/admin/action/run-full",   color: "#B71C1C" },
  ];

  const LOGS = ["phase1","phase2","phase3","aum_fetcher","ter_fetcher","cron_monthly"];

  // Triggers scripts/automation/monthly_audit.py directly (Group 8's own
  // audit system) -- a completely different script from monthly_runner.py,
  // which "Full Monthly Run" below triggers. Kept out of the ACTIONS list
  // and rendered inline in the Group 8 panel instead, via the same
  // runAction()/actionMsg mechanism every other action button uses, since
  // it belongs next to the diagnostic it produces the data for.
  const MONTHLY_AUDIT_ACTION = { key: "monthly-audit", label: "🗓️ Run Monthly Audit Now",
    endpoint: "/api/admin/action/run-monthly-audit" };

  const runDiag = async (key, endpoint) => {
    setLoading(l => ({...l, [key]: true}));
    try {
      const res = await fetch(API + endpoint, { headers });
      const data = await res.json();
      setResults(r => ({...r, [key]: data}));
    } catch(e) {
      setResults(r => ({...r, [key]: {overall:"error", checks:[{status:"error",label:"Request failed",detail:e.message}]}}));
    }
    setLoading(l => ({...l, [key]: false}));
  };

  const runAction = async (key, endpoint, label) => {
    setActionMsg(m => ({...m, [key]: "⏳ Starting..."}));
    try {
      const res = await fetch(API + endpoint, { method:"POST", headers });
      const data = await res.json();
      setActionMsg(m => ({...m, [key]: data.status === "started"
        ? "✅ " + data.message
        : "❌ " + (data.message || "Failed")}));
    } catch(e) {
      setActionMsg(m => ({...m, [key]: "❌ " + e.message}));
    }
  };

  const fetchLog = async (name) => {
    setLogName(name); setLogData(null);
    try {
      const res = await fetch(API + "/api/admin/diag/log/" + name, { headers });
      const data = await res.json();
      setLogData(data.lines || []);
    } catch(e) { setLogData(["Error: " + e.message]); }
  };

  const statusColor = (s) => s === "ok" ? "#2E7D32" : s === "warn" ? "#E65100" : "#B71C1C";
  const statusBg    = (s) => s === "ok" ? "#E8F5E9"  : s === "warn" ? "#FFF3E0"  : "#FFEBEE";
  const statusIcon  = (s) => s === "ok" ? "✅" : s === "warn" ? "⚠️" : "❌";
  const overallBg   = (s) => s === "ok" ? "#C8E6C9" : s === "warn" ? "#FFE0B2" : "#FFCDD2";

  const s = {
    wrap:     { fontFamily:"monospace", fontSize:"13px" },
    section:  { marginBottom:"28px" },
    groupHdr: { display:"flex", alignItems:"center", justifyContent:"space-between",
                padding:"10px 14px", borderRadius:"6px 6px 0 0", fontWeight:"700",
                fontSize:"14px", cursor:"pointer" },
    runBtn:   { padding:"5px 14px", borderRadius:"4px", border:"none", cursor:"pointer",
                background:"#1565C0", color:"#fff", fontSize:"12px", fontWeight:"600" },
    checkRow: { display:"flex", gap:"8px", alignItems:"flex-start",
                padding:"7px 12px", borderBottom:"1px solid #eee", fontSize:"12px" },
    checkLabel: { fontWeight:"600", minWidth:"280px" },
    checkDetail:{ color:"#555", flex:1 },
    actGrid:  { display:"flex", flexWrap:"wrap", gap:"10px", marginBottom:"8px" },
    actBtn:   { padding:"8px 16px", borderRadius:"5px", border:"none", cursor:"pointer",
                color:"#fff", fontWeight:"600", fontSize:"13px" },
    actMsg:   { fontSize:"12px", marginTop:"4px", padding:"4px 8px",
                background:"#F5F5F5", borderRadius:"4px" },
    logSel:   { display:"flex", gap:"8px", flexWrap:"wrap", marginBottom:"8px" },
    logBtn:   { padding:"4px 10px", borderRadius:"4px", border:"1px solid #90CAF9",
                background:"#E3F2FD", cursor:"pointer", fontSize:"12px" },
    logBox:   { background:"#1a1a1a", color:"#00FF41", padding:"12px", borderRadius:"6px",
                fontFamily:"monospace", fontSize:"11px", maxHeight:"300px",
                overflowY:"auto", whiteSpace:"pre-wrap" },
    mfdReportBtn: { padding:"9px 20px", background:"#2E7D32", color:"#fff",
                    borderRadius:"5px", border:"none", cursor:"pointer",
                    fontWeight:"700", fontSize:"14px", marginBottom:"16px" },
  };

  return (
    <div style={s.wrap}>

      {/* MFD Reports quick link */}
      <button style={s.mfdReportBtn}
        onClick={() => window.open("/mfd", "_blank")}>
        📊 Open MFD Reports Dashboard
      </button>

      {/* DIAGNOSTIC GROUPS */}
      {GROUPS.map(({key, label, endpoint}) => {
        const data = results[key];
        const overall = data?.overall || "idle";
        const hdrBg = data ? overallBg(overall) : "#ECEFF1";
        return (
          <div key={key} style={{...s.section, border:"1px solid #ddd", borderRadius:"6px"}}>
            <div style={{...s.groupHdr, background:hdrBg}}>
              <span>{data ? statusIcon(overall) : "○"} {label}
                {data && <span style={{marginLeft:"10px", fontWeight:"400", fontSize:"12px",
                  color:statusColor(overall)}}>{overall.toUpperCase()}</span>}
              </span>
              <button style={s.runBtn} onClick={() => runDiag(key, endpoint)}
                disabled={loading[key]}>
                {loading[key] ? "Running..." : "Run Check"}
              </button>
            </div>
            {key === "last-audit" && (
              <div style={{padding:"10px 14px", borderBottom:"1px solid #eee", background:"#FAFAFA"}}>
                <button style={{...s.actBtn, background:"#6A1B9A"}}
                  onClick={() => runAction(MONTHLY_AUDIT_ACTION.key, MONTHLY_AUDIT_ACTION.endpoint, MONTHLY_AUDIT_ACTION.label)}>
                  {MONTHLY_AUDIT_ACTION.label}
                </button>
                <div style={{fontSize:"11px", color:"#888", marginTop:"4px"}}>
                  Real run — inserts an audit_runs row and sends the report email, same as the 2nd-of-month cron.
                </div>
                {actionMsg[MONTHLY_AUDIT_ACTION.key] && <div style={s.actMsg}>{actionMsg[MONTHLY_AUDIT_ACTION.key]}</div>}
              </div>
            )}
            {data && (
              <div>
                {data.checks && data.checks.map((c,i) => (
                  <div key={i} style={{...s.checkRow, background:statusBg(c.status)}}>
                    <span>{statusIcon(c.status)}</span>
                    <span style={s.checkLabel}>{c.label}</span>
                    <span style={s.checkDetail}>{c.detail || ""}</span>
                  </div>
                ))}
                {data.month && <div style={{padding:"4px 12px", fontSize:"11px", color:"#888"}}>
                  Data month: {data.month}</div>}
              </div>
            )}
          </div>
        );
      })}

      {/* ACTION PANELS */}
      <div style={{...s.section, border:"1px solid #ddd", borderRadius:"6px"}}>
        <div style={{...s.groupHdr, background:"#E8EAF6"}}>
          ⚡ Action Panels — Manual Triggers
        </div>
        <div style={{padding:"14px"}}>
          <div style={s.actGrid}>
            {ACTIONS.map(({key, label, endpoint, color}) => (
              <div key={key}>
                <button style={{...s.actBtn, background:color}}
                  onClick={() => runAction(key, endpoint, label)}>
                  {label}
                </button>
                {actionMsg[key] && <div style={s.actMsg}>{actionMsg[key]}</div>}
              </div>
            ))}
          </div>
          <div style={{fontSize:"11px", color:"#888", marginTop:"8px"}}>
            ⚠️ Phase 2 and Full Pipeline take 30–60 minutes. They run in background — check logs below.
          </div>
        </div>
      </div>

      {/* LOG VIEWER */}
      <div style={{...s.section, border:"1px solid #ddd", borderRadius:"6px"}}>
        <div style={{...s.groupHdr, background:"#ECEFF1"}}>
          📄 Log Viewer — Last 100 Lines
        </div>
        <div style={{padding:"14px"}}>
          <div style={s.logSel}>
            {LOGS.map(name => (
              <button key={name} style={{...s.logBtn, fontWeight: logName===name ? "700":"400"}}
                onClick={() => fetchLog(name)}>
                {name}.log
              </button>
            ))}
          </div>
          {logData !== null && (
            <div style={s.logBox}>
              {logData.length === 0 ? "— log is empty —" : logData.join("\n")}
            </div>
          )}
        </div>
      </div>

    </div>
  );
}
